import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowsClockwise, CircleNotch, DownloadSimple, MagnifyingGlass, PencilSimple, Tag } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => (n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

const STATUS_LABEL = {
  pending_approval: { label: "Awaiting approval", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  verified_no_adjustment: { label: "Verified", className: "bg-[#F9FAFB] text-[#475467] border-[#E4E7EC]" },
  processing: { label: "Posting to SAP", className: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" },
  completed: { label: "Posted in SAP", className: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  partially_posted: { label: "Partly posted", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  stock_changed: { label: "Stock moved", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  rejected: { label: "Rejected", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
  failed: { label: "SAP refused", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
};

/** Admin fixing a wrongly counted quantity - the one correction the user asked
    for, and only while nothing of that record has reached SAP. */
const CorrectDialog = ({ record, onClose, onDone }) => {
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setQty(record ? String(record.physical_qty ?? "") : ""); setReason(""); }, [record]);

  const save = async () => {
    setSaving(true);
    try {
      const identifier = record.tag_no || `${record.product_id}@${record.warehouse_id}`;
      await axios.post(`${API}/physical-inventory/requests/${record.request_id}/correct/${encodeURIComponent(identifier)}`,
        { physical_qty: qty, reason });
      toast.success(`${record.tag_no ? `Tag ${record.tag_no}` : record.product_id} corrected to ${qty} ${record.uom}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not correct this count");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!record} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="pi-correct-dialog">
        <DialogHeader><DialogTitle className="font-heading text-base">Correct counted quantity</DialogTitle></DialogHeader>
        {record && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <span className="text-[#667085]">Tag</span><span className="font-mono font-bold text-[#101828]">{record.tag_no || "no tag - counted without tags"}</span>
              <span className="text-[#667085]">Record</span><span className="font-mono text-[#344054]">{record.request_id}</span>
              <span className="text-[#667085]">Item</span><span className="text-[#344054]">{record.product_id} · {record.description || "—"}</span>
              <span className="text-[#667085]">Counted by</span><span className="text-[#344054]">{record.counted_by || "—"}</span>
              <span className="text-[#667085]">Warehouse</span><span className="font-mono text-[#344054]">{record.warehouse_id}</span>
              <span className="text-[#667085]">System qty</span><span className="tabular-nums text-[#344054]">{fmt(record.current_qty)} {record.uom}</span>
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Corrected physical quantity ({record.uom})</Label>
              <Input type="number" step="any" min="0" value={qty} onChange={(e) => setQty(e.target.value)}
                className="h-9" data-testid="pi-correct-qty-input" />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Why</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. counter wrote 120 instead of 102" data-testid="pi-correct-reason-input" />
            </div>
            <p className="text-[11px] text-[#667085] bg-[#F9FAFB] border border-[#E4E7EC] rounded-lg p-2.5">
              Only the quantity changes. The item, warehouse and tag stay as counted, and the original figure is kept on
              the record. Nothing has been posted to SAP for this count yet.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pi-correct-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !String(qty).trim() || !reason.trim()}
            className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="pi-correct-save">
            {saving ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null} Save correction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** The Audit tab: what was submitted, by whom, against which tag - and the
    same thing as a file. A counter only ever sees their own records; an
    administrator can switch to every site's. */
export const PhysicalInventoryCountAudit = ({ sites, isAdmin, tagRequired, onTagSettingChange }) => {
  const [scope, setScope] = useState(isAdmin ? "all" : "mine");
  const [siteId, setSiteId] = useState("all");
  const [search, setSearch] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const [savingTagSetting, setSavingTagSetting] = useState(false);

  const params = useMemo(() => ({
    scope, ...(siteId !== "all" ? { site_id: siteId } : {}), ...(search.trim() ? { search: search.trim() } : {}),
  }), [scope, siteId, search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/physical-inventory/audit-records`, { params });
      setRecords(data.records || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the audit records");
      setRecords([]);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.get(`${API}/physical-inventory/audit-records/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `physical-inventory-audit-${siteId === "all" ? "all" : siteId.toLowerCase()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${records.length} record(s) downloaded`);
    } catch (e) {
      toast.error("Could not build the file");
    } finally { setDownloading(false); }
  };

  const toggleTag = async (site, required) => {
    setSavingTagSetting(true);
    try {
      await axios.put(`${API}/physical-inventory/tag-settings/${site}`, { tag_required: required });
      onTagSettingChange(site, required);
      toast.success(`${site} now counts ${required ? "with" : "without"} tag numbers`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change the tag setting");
    } finally { setSavingTagSetting(false); }
  };

  // Deliberately NOT falling back to the first site: an administrator must pick
  // the site before its tag switch appears, or P1 gets flipped by accident.
  const tagSite = siteId === "all" ? "" : siteId;

  return (
    <div className="space-y-4" data-testid="pi-audit-tab">
      <div className="bg-white border border-[#E4E7EC] rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          {isAdmin && (
            <div className="w-full sm:w-48">
              <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Records</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="bg-white" data-testid="pi-audit-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone's counts</SelectItem>
                  <SelectItem value="mine">Only mine</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="w-full sm:w-40">
            <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Site</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger className="bg-white" data-testid="pi-audit-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sites</SelectItem>
                {(sites || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Search</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8"
                placeholder="Tag no, item, warehouse, user or record" data-testid="pi-audit-search" />
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="pi-audit-refresh">
            {loading ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <ArrowsClockwise size={14} className="mr-1.5" />}
            Refresh
          </Button>
          <Button size="sm" onClick={download} disabled={downloading || !records.length}
            className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="pi-audit-download">
            {downloading ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <DownloadSimple size={14} className="mr-1.5" />}
            Download
          </Button>
        </div>

        {isAdmin && !tagSite && (
          <p className="text-[11px] text-[#98A2B3] mt-4 pt-3 border-t border-[#F2F4F7]" data-testid="pi-tag-control-hint">
            Pick a single site above to switch its tag control (with tag / without tag). Every site counts with tag numbers by default.
          </p>
        )}

        {isAdmin && tagSite && (
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-[#F2F4F7]" data-testid="pi-tag-control">
            <Tag size={16} weight="fill" className="text-[#0E7C86]" />
            <div className="text-xs">
              <span className="font-bold text-[#344054]">Tag control</span>
              <span className="text-[#667085]"> · counting at {tagSite} {(tagRequired || {})[tagSite] === false ? "does not ask for a tag number" : "requires a tag number on every line"}</span>
            </div>
            <Switch
              checked={(tagRequired || {})[tagSite] !== false}
              disabled={savingTagSetting}
              onCheckedChange={(v) => toggleTag(tagSite, v)}
              data-testid="pi-tag-switch"
            />
            <span className="text-[11px] text-[#98A2B3]">Applies to {tagSite} only. Default is with tag.</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
        <div className="max-h-[62vh] overflow-auto">
          <Table className="min-w-[1180px]">
            <TableHeader className="sticky top-0 z-10 bg-[#F9FAFB]">
              <TableRow className="bg-[#F9FAFB]">
                <TableHead className="text-xs">Tag No</TableHead>
                <TableHead className="text-xs">Record</TableHead>
                <TableHead className="text-xs">Submitted</TableHead>
                <TableHead className="text-xs">User name</TableHead>
                <TableHead className="text-xs">Site</TableHead>
                <TableHead className="text-xs">Warehouse</TableHead>
                <TableHead className="text-xs">Item</TableHead>
                <TableHead className="text-xs">Item name</TableHead>
                <TableHead className="text-xs text-right">Qty</TableHead>
                <TableHead className="text-xs text-right">System</TableHead>
                <TableHead className="text-xs text-right">Difference</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                {isAdmin && <TableHead className="text-xs w-24">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={isAdmin ? 13 : 12} className="text-sm text-[#667085] py-8 text-center" data-testid="pi-audit-loading">Loading records...</TableCell></TableRow>}
              {!loading && !records.length && (
                <TableRow><TableCell colSpan={isAdmin ? 13 : 12} className="text-sm text-[#667085] py-8 text-center" data-testid="pi-audit-empty">
                  Nothing submitted yet{scope === "mine" ? " by you" : ""}{siteId !== "all" ? ` at ${siteId}` : ""}.
                </TableCell></TableRow>
              )}
              {!loading && records.map((r, i) => (
                <TableRow key={`${r.request_id}-${r.tag_no || r.product_id}-${i}`} data-testid={`pi-audit-row-${i}`}>
                  <TableCell className="font-mono text-xs font-bold text-[#101828]" data-testid={`pi-audit-tag-${i}`}>{r.tag_no || "—"}</TableCell>
                  <TableCell className="font-mono text-[11px] text-[#475467]">{r.request_id}</TableCell>
                  <TableCell className="text-[11px] text-[#667085] whitespace-nowrap">{r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-IN") : "—"}</TableCell>
                  <TableCell className="text-xs text-[#344054]">{r.counted_by || "—"}</TableCell>
                  <TableCell className="text-xs text-[#344054]">{r.site_id}</TableCell>
                  <TableCell className="text-xs text-[#344054]"><span className="font-mono">{r.warehouse_id}</span></TableCell>
                  <TableCell className="font-mono text-xs text-[#101828]">{r.product_id}</TableCell>
                  <TableCell className="text-xs text-[#475467] max-w-[220px] truncate" title={r.description}>{r.description || "—"}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-bold" data-testid={`pi-audit-qty-${i}`}>
                    {fmt(r.physical_qty)} <span className="text-[#98A2B3] font-normal">{r.uom}</span>
                    {r.corrected && (
                      <span className="block text-[10px] text-[#B54708]" title={`was ${fmt(r.original_physical_qty)}`}>corrected</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-right tabular-nums text-[#667085]">{fmt(r.current_qty)}</TableCell>
                  <TableCell className={`text-xs text-right tabular-nums font-bold ${r.difference < 0 ? "text-[#B42318]" : r.difference > 0 ? "text-[#027A48]" : "text-[#667085]"}`}>
                    {r.difference > 0 ? `+${fmt(r.difference)}` : fmt(r.difference)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${(STATUS_LABEL[r.status] || {}).className || ""}`}>
                      {(STATUS_LABEL[r.status] || { label: r.status }).label}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {r.correctable ? (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setCorrecting(r)}
                          data-testid={`pi-audit-correct-${i}`}>
                          <PencilSimple size={12} className="mr-1" /> Qty
                        </Button>
                      ) : (
                        <span className="text-[10px] text-[#98A2B3]" title="A count can only be corrected before it posts to SAP">locked</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!!records.length && (
          <div className="px-4 py-2 border-t border-[#E4E7EC] text-[11px] text-[#667085]" data-testid="pi-audit-count">
            {records.length} counted line(s){scope === "mine" ? " submitted by you" : ""}
          </div>
        )}
      </div>

      <CorrectDialog record={correcting} onClose={() => setCorrecting(null)} onDone={load} />
    </div>
  );
};
